import createCache from '@emotion/cache'
import type { EmotionCache } from '@emotion/cache'
import type { QueryClient } from '@tanstack/react-query'
import { parseCookie } from 'cookie-es'
import { createStore, getDefaultStore } from 'jotai'
import {
  createContext,
  useContext,
  type PropsWithChildren,
  createElement,
} from 'react'
import { createQueryClient } from 'src/services/query'

export type RequestColorMode = 'light' | 'dark'

export interface RequestContextValue {
  emotionCache: EmotionCache
  queryClient: QueryClient
  jotaiStore: ReturnType<typeof createStore>
  colorMode: RequestColorMode
  mayTokenBeValid: boolean
}

const RequestContext = createContext<RequestContextValue | null>(null)

export function readColorModeFromCookie(
  cookieHeader?: string | null,
): RequestColorMode {
  return parseCookie(cookieHeader ?? '')['color-mode'] === 'dark'
    ? 'dark'
    : 'light'
}

export function readTokenHintFromCookie(cookieHeader?: string | null): boolean {
  const cookies = parseCookie(cookieHeader ?? '')
  // Server-set with a real Expires= so the browser purges it when user_token expires.
  // Prefer it over token_hint, which has a 365-day TTL and can be stale after logout.
  const expireAt = cookies['user_token_expire_at']
  if (expireAt != null) {
    return Number(expireAt) > Date.now()
  }
  return cookies['token_hint'] === '1'
}

export function createEmotionCache(): EmotionCache {
  // No insertionPoint: holding a ref to the <meta> node causes a prod-only
  // insertBefore crash when React detaches it during <head> reconciliation.
  // prepend: true re-anchors to container.firstChild on every insert instead.
  //
  // key: 'css' matches emotion's default so SSR (which bypasses CacheProvider)
  // and the client hydration pass emit identical class names. Using 'mui' here
  // caused className mismatches on every MUI component and a full grid re-render.
  return createCache({
    key: 'css',
    prepend: true,
  })
}

export function createRequestContextValue(
  overrides: Partial<
    Pick<RequestContextValue, 'colorMode' | 'mayTokenBeValid'>
  > = {},
): RequestContextValue {
  const browserCookies =
    typeof document !== 'undefined' ? document.cookie : undefined

  return {
    emotionCache: createEmotionCache(),
    queryClient: createQueryClient(),
    jotaiStore:
      typeof window !== 'undefined' ? getDefaultStore() : createStore(),
    colorMode:
      overrides.colorMode ?? readColorModeFromCookie(browserCookies ?? null),
    mayTokenBeValid:
      overrides.mayTokenBeValid ??
      readTokenHintFromCookie(browserCookies ?? null),
  }
}

export function RequestContextProvider({
  value,
  children,
}: PropsWithChildren<{ value: RequestContextValue }>) {
  return createElement(RequestContext.Provider, { value }, children)
}

export function useOptionalRequestContext() {
  return useContext(RequestContext)
}

export function useRequestContext() {
  const context = useOptionalRequestContext()
  if (!context) {
    throw new Error('RequestContext is not available')
  }
  return context
}
